import dayjs from "dayjs";
import { createEmptyAddTenorRow } from "constants/addTenorTable";
import { autofillSubLegsForRow } from "./addTenorTenorRules";
import { normalizeSublegMonthLabels } from "./optionRowTenorCreation";

export function createTenorGroupId() {
  return `tenor-group-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isAddTenorContinuationRow(row) {
  return (
    row?.tenor_group_id != null &&
    Math.max(0, Number(row.sub_leg_index) || 0) > 0
  );
}

/** Primary row for a multi-month tenor group (sub_leg_index === 0). */
export function getAddTenorGroupPrimaryRow(rows, row) {
  if (!row?.tenor_group_id || !Array.isArray(rows)) return row ?? null;
  return (
    rows.find(
      (item) =>
        item.tenor_group_id === row.tenor_group_id &&
        Math.max(0, Number(item.sub_leg_index) || 0) === 0
    ) ?? row
  );
}

/** True when the selected catalog tenor already exists (update mode). */
export function isAddTenorExistingCatalogTenorRow(row, rows) {
  if (!row) return false;
  const primary = getAddTenorGroupPrimaryRow(rows, row);
  return Boolean(primary?.tenor_exists);
}

export function isAddTenorGroupedRow(row) {
  return row?.tenor_group_id != null;
}

export function getSublegLabelsForTenorSelection(row, option, shortcutLabel = "") {
  if (option?.exists === true) {
    const savedLabels = normalizeSublegMonthLabels(
      (option.tenor_months ?? []).map((item) => item?.subleg_month)
    );
    if (savedLabels.length) return savedLabels;
  }

  const apiLabels = normalizeSublegMonthLabels(option?.subleg_months);
  if (apiLabels.length) return apiLabels;

  const months = Math.max(
    1,
    Number(option?.sublegs ?? row.months_in_tenor) || 1
  );
  const nextRow = { ...row, months_in_tenor: months };
  return autofillSubLegsForRow(nextRow, shortcutLabel);
}

function parseTenorMonthDate(value) {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

function datesForSublegRow(baseRow, sublegLabel, index, option) {
  const enableStart = Boolean(baseRow.enable_start_date);
  const tenorMonths = Array.isArray(option?.tenor_months)
    ? option.tenor_months
    : [];
  const label = String(sublegLabel ?? "").trim();
  const match =
    (label
      ? tenorMonths.find(
          (item) => String(item?.subleg_month ?? "").trim() === label
        )
      : null) ?? tenorMonths[index];

  if (match) {
    return {
      start_date: enableStart ? parseTenorMonthDate(match.start_date) : null,
      end_date: parseTenorMonthDate(match.end_date),
    };
  }

  return {
    start_date:
      enableStart && index === 0 ? baseRow.start_date ?? null : null,
    end_date: null,
  };
}

export function buildExpandedSublegRow(
  baseRow,
  sublegLabel,
  index,
  total,
  groupId,
  shortcutLabel = "",
  option = null
) {
  const label = String(sublegLabel ?? "").trim();
  const dates = datesForSublegRow(
    baseRow,
    label || baseRow.tenor_label,
    index,
    option
  );

  return {
    ...baseRow,
    key: index === 0 ? baseRow.key : createEmptyAddTenorRow().key,
    tenor_group_id: groupId,
    sub_leg_index: index,
    sub_leg: label,
    months_in_tenor: total,
    sub_legs: [],
    start_date: dates.start_date,
    end_date: dates.end_date,
  };
}

export function buildExpandedSublegRows(
  patchedRow,
  option,
  shortcutLabel = ""
) {
  const months = Math.max(1, Number(patchedRow.months_in_tenor) || 1);
  if (months <= 1) {
    const dates = datesForSublegRow(
      patchedRow,
      patchedRow.tenor_label,
      0,
      option
    );
    return [
      {
        ...patchedRow,
        tenor_group_id: null,
        sub_leg_index: 0,
        sub_leg: "",
        sub_legs: [],
        start_date: dates.start_date,
        end_date: dates.end_date,
      },
    ];
  }

  const labels = getSublegLabelsForTenorSelection(
    patchedRow,
    option,
    shortcutLabel
  );
  const groupId = patchedRow.tenor_group_id ?? createTenorGroupId();
  const autofillLabels = autofillSubLegsForRow(
    { ...patchedRow, months_in_tenor: months },
    shortcutLabel
  );
  const subLegLabels = Array.from({ length: months }, (_, index) => {
    return labels[index] ?? autofillLabels[index] ?? "";
  });

  return subLegLabels.map((label, index) =>
    buildExpandedSublegRow(
      patchedRow,
      label,
      index,
      months,
      groupId,
      shortcutLabel,
      option
    )
  );
}

/**
 * Replace one table row with one or more sub-leg rows after tenor selection.
 */
export function replaceRowWithExpandedSublegRows(
  rows,
  rowKey,
  patchedRow,
  option,
  shortcutLabel = ""
) {
  const index = rows.findIndex((row) => row.key === rowKey);
  if (index < 0) return rows;

  const existingRow = rows[index];
  const expanded = buildExpandedSublegRows(patchedRow, option, shortcutLabel);

  if (existingRow.tenor_group_id) {
    const sliceEnd = rows.findIndex(
      (item, idx) =>
        idx > index && item.tenor_group_id !== existingRow.tenor_group_id
    );
    const end = sliceEnd === -1 ? rows.length : sliceEnd;
    return [...rows.slice(0, index), ...expanded, ...rows.slice(end)];
  }

  return [...rows.slice(0, index), ...expanded, ...rows.slice(index + 1)];
}

/**
 * Re-expand the tenor group when # months changes on the primary row.
 */
export function reexpandTenorGroupRows(rows, rowKey, months, shortcutLabel = "") {
  const index = rows.findIndex((row) => row.key === rowKey);
  if (index < 0) return rows;

  const row = rows[index];
  if (!row.tenor_label) {
    return rows.map((current) =>
      current.key === rowKey
        ? {
            ...current,
            months_in_tenor: months,
            sub_leg: "",
            sub_legs: [],
            tenor_group_id: null,
            sub_leg_index: 0,
          }
        : current
    );
  }

  const patchedRow = {
    ...row,
    months_in_tenor: months,
    tenor_group_id: months > 1 ? row.tenor_group_id ?? createTenorGroupId() : null,
  };
  const expanded = buildExpandedSublegRows(patchedRow, null, shortcutLabel);

  if (row.tenor_group_id) {
    const sliceEnd = rows.findIndex(
      (item, idx) => idx > index && item.tenor_group_id !== row.tenor_group_id
    );
    const end = sliceEnd === -1 ? rows.length : sliceEnd;
    return [...rows.slice(0, index), ...expanded, ...rows.slice(end)];
  }

  return replaceRowWithExpandedSublegRows(
    rows,
    rowKey,
    patchedRow,
    null,
    shortcutLabel
  );
}

export function removeTenorGroupRows(rows, rowKey) {
  const row = rows.find((item) => item.key === rowKey);
  if (!row?.tenor_group_id) {
    return rows.filter((item) => item.key !== rowKey);
  }
  return rows.filter((item) => item.tenor_group_id !== row.tenor_group_id);
}

/** Distinct tenor blocks (standalone rows or one multi-month group). */
export function countAddTenorBlocks(rows) {
  return groupAddTenorRowsForSubmit(rows).length;
}

/** Remove is only on primary rows; at least one block must remain. */
export function canRemoveAddTenorBlock(rows, rowKey) {
  if (!Array.isArray(rows) || countAddTenorBlocks(rows) <= 1) return false;
  const row = rows.find((item) => item.key === rowKey);
  if (!row || isAddTenorContinuationRow(row)) return false;
  return true;
}

/** @returns {object[][]} */
export function groupAddTenorRowsForSubmit(rows) {
  const grouped = new Map();
  const standalone = [];

  rows.forEach((row) => {
    if (row.tenor_group_id) {
      const groupId = row.tenor_group_id;
      if (!grouped.has(groupId)) grouped.set(groupId, []);
      grouped.get(groupId).push(row);
      return;
    }
    standalone.push([row]);
  });

  const groupedRows = [...grouped.values()].map((group) =>
    [...group].sort(
      (a, b) =>
        Math.max(0, Number(a.sub_leg_index) || 0) -
        Math.max(0, Number(b.sub_leg_index) || 0)
    )
  );

  return [...standalone, ...groupedRows];
}
